import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  DELETE_CONVERSATION_ACTION,
  DELETE_CONVERSATION_CANCEL,
  DELETE_CONVERSATION_DESCRIPTION,
  DELETE_CONVERSATION_HEADER,
  DELETE_CONVERSATION_SUBHEADER,
  DELETE_CONVERSATION_TITLE,
  DELETE_GROUP_OWNER_ACTION,
  DELETE_GROUP_OWNER_HEADER,
  DELETE_GROUP_OWNER_SUBHEADER,
  DELETE_GROUP_OWNER_TITLE,
  LEAVE_GROUP_ACTION,
  LEAVE_GROUP_DESCRIPTION,
  LEAVE_GROUP_TITLE,
} from "@/constants/ui";
import {
  deleteConversation,
  selectConversations,
  selectCurrConversation,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useState } from "react";

import { ERROR_TITLE_GENERIC } from "@/constants/errors";
import { Trash2 } from "lucide-react";
import { getGroups } from "@/services/userGroupService";
import { selectUsername } from "@/features/auth/authSlice";
import { setMessages } from "@/features/chat/chatSlice";
import { useToast } from "../ui/use-toast";

/**
 * Deletes the open conversation, from inside the details pane (alongside Search and Members).
 *
 * DeleteUserThread does two different things to a group depending on who asks
 * (dbfuncs_admin.php, DBAdminDeleteGroupMessages): an owner deletes it for every member,
 * anyone else is just removed from it. The wording follows that split, and falls back to the
 * ambiguous "if you are the owner..." copy when ownership can't be resolved — never the
 * reassuring "you'll just leave" wording, which would understate what happens.
 */
const DeleteButton = () => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const conversations = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const username = useAppSelector(selectUsername);
  // `!== null`, not truthiness: index 0 is a valid conversation — and the usual one, since the
  // list is sorted newest-first — which is why deleting from this pane appeared to do nothing.
  const currConversation =
    currConversationIndex !== null ? conversations[currConversationIndex] : null;

  const isGroup = currConversation?.type === "Group";
  const address = currConversation?.address ?? "";
  const groupName = currConversation?.groupName || "";
  const listedOwnerName = currConversation?.ownerName || "";
  const [fetchedOwnerName, setFetchedOwnerName] = useState("");

  // GetConversations doesn't return ownerName, so after a page reload fall back to the groups
  // list — TTL-cached and already warmed by opening the conversation, so usually free.
  useEffect(() => {
    if (!isGroup || listedOwnerName || !address) {
      setFetchedOwnerName("");
      return;
    }
    let cancelled = false;
    getGroups()
      .then((groups) => {
        if (cancelled) return;
        const group = groups.find((g) => g.address === address || g.name === groupName);
        setFetchedOwnerName(group?.ownerName ?? "");
      })
      .catch((err) => {
        console.error("[DeleteConversation] Could not resolve group owner:", err);
        if (!cancelled) setFetchedOwnerName("");
      });
    return () => {
      cancelled = true;
    };
  }, [isGroup, address, groupName, listedOwnerName]);

  const ownerName = listedOwnerName || fetchedOwnerName;
  const isGroupOwner = isGroup && !!username && ownerName === username;
  const isGroupMember = isGroup && !!ownerName && !isGroupOwner;

  // On success, one state update lands the user on the no-conversation-selected screen: row
  // removed + selection cleared (controlSlice), pane closed (detailsSlice), cache dropped
  // (conversationCacheSlice) — all off deleteConversation/fulfilled, so no intermediate frame.
  const handleDeleteConversation = async () => {
    if (!currConversation) return;
    const result = await dispatch(deleteConversation(currConversation.address));

    if (deleteConversation.rejected.match(result)) {
      // Refusals come back 200 with an error body, so they used to read as success and the
      // conversation silently reappeared. Tell the user and leave them where they were.
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: result.payload ?? "Failed to delete this conversation. Please try again.",
      });
      return;
    }

    // The pane is already unmounted; this just stops chat state holding the deleted rows.
    dispatch(setMessages([]));
  };

  // A group whose ownership didn't resolve falls through to the generic copy, which covers
  // either outcome.
  const copy = isGroupOwner
    ? {
        title: DELETE_GROUP_OWNER_TITLE,
        action: DELETE_GROUP_OWNER_ACTION,
        body: [DELETE_GROUP_OWNER_HEADER, DELETE_GROUP_OWNER_SUBHEADER],
      }
    : isGroupMember
      ? { title: LEAVE_GROUP_TITLE, action: LEAVE_GROUP_ACTION, body: [LEAVE_GROUP_DESCRIPTION] }
      : {
          title: DELETE_CONVERSATION_TITLE,
          action: DELETE_CONVERSATION_ACTION,
          body: isGroup
            ? [DELETE_CONVERSATION_HEADER, DELETE_CONVERSATION_SUBHEADER]
            : [DELETE_CONVERSATION_DESCRIPTION],
        };

  return (
    <AlertDialog>
      <AlertDialogTrigger>
        <div className="p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-gray-200">
          <Trash2 size={20} />
        </div>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          {/* asChild + div: the default renders a <p>, which can't nest these paragraphs. */}
          <AlertDialogDescription className="flex flex-col gap-4" asChild>
            <div>
              {copy.body.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{DELETE_CONVERSATION_CANCEL}</AlertDialogCancel>
          <AlertDialogAction
            className={isGroupOwner ? "bg-red-500 hover:bg-red-500/90" : undefined}
            onClick={() => handleDeleteConversation()}
          >
            {copy.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteButton;
